// ?overlay: a driver-inputs panel (steering wheel, pedals, paddles, gear,
// force feedback) like the input telemetry on TV onboards. Reads the mapped
// wheel state, so it shows exactly what the car receives.

import type { WheelManager } from '../wheel/wheelManager';
import { wheelParams } from '../wheel/settings';

export class InputOverlay {
  private readonly el: HTMLElement;
  private readonly rim: SVGGElement;
  private readonly q = <T extends Element>(sel: string): T => this.el.querySelector(sel) as T;

  constructor(
    root: HTMLElement,
    private readonly wheel: WheelManager,
    label = 'Simulated G27 · scripted driver',
  ) {
    this.el = document.createElement('div');
    this.el.className = 'input-overlay';
    this.el.innerHTML = `
      <svg class="io-wheel" viewBox="-110 -80 220 160" aria-hidden="true">
        <g class="io-rim">
          <path d="M -95 -30 Q -100 -62 -60 -66 L 60 -66 Q 100 -62 95 -30 L 88 40 Q 84 62 58 62 L 30 62 Q 18 62 12 50 L -12 50 Q -18 62 -30 62 L -58 62 Q -84 62 -88 40 Z" class="io-body"/>
          <rect x="-46" y="-40" width="92" height="40" rx="6" class="io-screen"/>
          <text x="0" y="-12" class="io-gear">N</text>
          <circle cx="-62" cy="-30" r="7" class="io-btn"/><circle cx="62" cy="-30" r="7" class="io-btn"/>
          <circle cx="-66" cy="0" r="6" class="io-btn"/><circle cx="66" cy="0" r="6" class="io-btn"/>
          <rect x="-102" y="-20" width="10" height="44" rx="4" class="io-paddle io-paddle-l"/>
          <rect x="92" y="-20" width="10" height="44" rx="4" class="io-paddle io-paddle-r"/>
          <line x1="0" y1="-78" x2="0" y2="-66" class="io-top"/>
        </g>
      </svg>
      <div class="io-side">
        <div class="io-angle">0°</div>
        <div class="io-pedals">
          <div class="io-pedal" data-p="brake"><i></i><span>BRK</span></div>
          <div class="io-pedal" data-p="throttle"><i></i><span>THR</span></div>
        </div>
        <div class="io-ffb"><i></i><b></b><span>FFB</span></div>
      </div>
      <div class="io-label">${label}</div>`;
    root.appendChild(this.el);
    this.rim = this.q<SVGGElement>('.io-rim');
  }

  update(gearLabel: string, speedKmh: number): void {
    const s = this.wheel.state;
    const steer = s?.steer ?? 0;
    const deg = (steer * wheelParams.rotation.value) / 2;
    this.rim.setAttribute('transform', `rotate(${deg.toFixed(1)})`);
    this.q('.io-angle').textContent = `${deg >= 0 ? '' : '−'}${Math.abs(Math.round(deg))}° · ${Math.round(speedKmh)} km/h`;
    this.q('.io-gear').textContent = gearLabel;
    (this.q('[data-p="throttle"] i') as HTMLElement).style.height = `${Math.round((s?.throttle ?? 0) * 100)}%`;
    (this.q('[data-p="brake"] i') as HTMLElement).style.height = `${Math.round((s?.brake ?? 0) * 100)}%`;
    const p = this.wheel.profile.buttons;
    const c = this.wheel.source?.controls;
    this.q('.io-paddle-r').classList.toggle('on', !!(c && p.upshift !== undefined && c.buttons[p.upshift]));
    this.q('.io-paddle-l').classList.toggle('on', !!(c && p.downshift !== undefined && c.buttons[p.downshift]));
    // Force (+ = turning the rim right), as a bar from the centre.
    const f = Math.max(-1, Math.min(1, this.wheel.ffb.lastTotal));
    const bar = this.q('.io-ffb i') as HTMLElement;
    bar.style.left = `${50 + Math.min(0, f) * 50}%`;
    bar.style.width = `${Math.abs(f) * 50}%`;
  }
}
